/**
 * App-tour state.
 *
 * Desktop/web only — `TourProvider` is mounted by DesktopLayout, which never
 * renders on native. It is safe to call `useTour()` from a shared component
 * regardless: outside a provider the hook returns an inert tour whose
 * `available` is false, so callers render nothing rather than crashing.
 *
 * The provider owns three things:
 *   1. Which steps this user gets (computed once, at start — see `steps.ts`).
 *   2. Where they are in that list.
 *   3. Whether they have ever taken the tour, so the launcher can carry a
 *      first-run dot. Nothing here ever starts the tour on its own.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import { TOUR_STEPS } from './steps';
import type { TourAudience, TourStep } from './types';
import type { RootState } from '../store';

/** Set once the user opens the tour — drives the launcher's first-run dot. */
export const TOUR_SEEN_KEY = '@eleplan/app_tour_seen';

interface TourValue {
  /** False when there is no provider (native), so launchers can hide. */
  available: boolean;
  active: boolean;
  /** Steps this user gets — already filtered by `appliesTo`. */
  steps: TourStep[];
  index: number;
  step: TourStep | null;
  /** True until the user has opened the tour at least once. */
  isFirstRun: boolean;
  start: () => void;
  next: () => void;
  prev: () => void;
  end: () => void;
  /** Drop the current step and advance — used when an anchor never appears. */
  skipCurrent: () => void;
}

const INERT: TourValue = {
  available: false,
  active: false,
  steps: [],
  index: 0,
  step: null,
  isFirstRun: false,
  start: () => {},
  next: () => {},
  prev: () => {},
  end: () => {},
  skipCurrent: () => {},
};

const TourContext = createContext<TourValue>(INERT);

export function useTour(): TourValue {
  return useContext(TourContext);
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);
  const [isFirstRun, setIsFirstRun] = useState(false);

  const projects = useSelector((s: RootState) => (s as any)?.projects?.projects);
  const account = useSelector((s: RootState) => (s as any)?.session?.account);

  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(TOUR_SEEN_KEY)
      .then((v) => { if (!cancelled) setIsFirstRun(v !== 'true'); })
      .catch(() => {/* a missing flag just means no dot */});
    return () => { cancelled = true; };
  }, []);

  const audience: TourAudience = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    const first: any = list[0] ?? null;
    return {
      hasProjects: list.length > 0,
      firstProjectId: first?.id ?? first?.project_id ?? null,
      firstProjectName: first?.first_name || first?.project_name || '',
      isPartner: !!(account as any)?.is_partner,
      // Mirrors the sidebar's plan-count gate (MIN_PLANS_FOR_ACCOUNT_CHAT = 3):
      // the Ellie orb only renders once the account has enough plans for account-level chat.
      hasAccountChat: list.length >= 3,
    };
  }, [projects, account]);

  const start = useCallback(() => {
    const applicable = TOUR_STEPS.filter((s) => !s.appliesTo || s.appliesTo(audience));
    if (!applicable.length) return;
    setSteps(applicable);
    setIndex(0);
    setActive(true);
    setIsFirstRun(false);
    AsyncStorage.setItem(TOUR_SEEN_KEY, 'true').catch(() => {});
    applicable[0].navigate?.(audience);
  }, [audience]);

  const end = useCallback(() => {
    setActive(false);
    setIndex(0);
  }, []);

  const goTo = useCallback((nextIndex: number) => {
    setIndex((current) => {
      const target = Math.max(0, nextIndex);
      if (target >= steps.length) {
        // Walking off the end is how the tour finishes.
        setActive(false);
        return 0;
      }
      if (target !== current) steps[target]?.navigate?.(audience);
      return target;
    });
  }, [steps, audience]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  /**
   * Remove the current step and show the next one. The overlay calls this when
   * an anchor never materialises — a step pointing at something that isn't
   * there should vanish from the tour, including from the "n of m" counter,
   * rather than sit on a blank spotlight.
   */
  const skipCurrent = useCallback(() => {
    setSteps((current) => {
      const remaining = current.filter((_, i) => i !== index);
      if (!remaining.length) setActive(false);
      else {
        const target = Math.min(index, remaining.length - 1);
        setIndex(target);
        remaining[target]?.navigate?.(audience);
      }
      return remaining;
    });
  }, [index, audience]);

  const value = useMemo<TourValue>(() => ({
    available: true,
    active,
    steps,
    index,
    step: active ? steps[index] ?? null : null,
    isFirstRun,
    start,
    next,
    prev,
    end,
    skipCurrent,
  }), [active, steps, index, isFirstRun, start, next, prev, end, skipCurrent]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
